import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import { getCompanyBranding, updateCompanyBranding, uploadCompanyLogo, deleteCompanyLogo } from '../../../services/companySettingsService';

const CompanyBrandingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [branding, setBranding] = useState({
    id: null,
    companyName: '',
    logoUrl: '',
    logoFilePath: '',
    watermarkEnabled: false,
    watermarkOpacity: 0.15,
    watermarkPosition: 'center'
  });
  const [originalBranding, setOriginalBranding] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const watermarkPositionOptions = [
    { value: 'center', label: 'مركز' },
    { value: 'diagonal', label: 'قطري' },
    { value: 'bottom-right', label: 'أسفل اليمين' },
    { value: 'top-left', label: 'أعلى اليسار' }
  ];

  useEffect(() => {
    loadBranding();
  }, []);

  useEffect(() => {
    if (originalBranding) {
      const changed = JSON.stringify(branding) !== JSON.stringify(originalBranding);
      setHasChanges(changed);
    }
  }, [branding, originalBranding]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const data = await getCompanyBranding();
      setBranding(data);
      setOriginalBranding(JSON.parse(JSON.stringify(data)));
      setLogoPreview(data?.logoUrl);
    } catch (error) {
      console.error('Error loading branding:', error);
      alert('فشل في تحميل إعدادات العلامة التجارية');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
      setHasChanges(true);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const result = await uploadCompanyLogo(selectedFile);
      
      // Delete old logo if exists
      if (branding?.logoFilePath) {
        try {
          await deleteCompanyLogo(branding?.logoFilePath);
        } catch (error) {
          console.warn('Could not delete old logo:', error);
        }
      }

      setBranding(prev => ({
        ...prev,
        logoUrl: result?.publicUrl,
        logoFilePath: result?.filePath
      }));
      setSelectedFile(null);
      alert('تم رفع الشعار بنجاح');
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert(error?.message || 'فشل في رفع الشعار');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Upload logo first if file selected
      if (selectedFile) {
        await handleUploadLogo();
      }

      const updated = await updateCompanyBranding(branding);
      setBranding(updated);
      setOriginalBranding(JSON.parse(JSON.stringify(updated)));
      setHasChanges(false);
      alert('تم حفظ التغييرات بنجاح');
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('فشل في حفظ التغييرات');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setBranding(JSON.parse(JSON.stringify(originalBranding)));
    setLogoPreview(originalBranding?.logoUrl);
    setSelectedFile(null);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[var(--color-primary)]/10 rounded-lg flex items-center justify-center">
            <Icon name="Building2" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-[var(--color-foreground)]">
              إعدادات العلامة التجارية
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              إدارة اسم الشركة والشعار للظهور في التقارير والعقود والرسائل
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            label="اسم الشركة"
            type="text"
            value={branding?.companyName}
            onChange={(e) => handleChange('companyName', e?.target?.value)}
            placeholder="أدخل اسم الشركة"
          />

          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
              شعار الشركة
            </label>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                {logoPreview ? (
                  <div className="w-32 h-32 rounded-lg border-2 border-[var(--color-border)] overflow-hidden bg-white flex items-center justify-center">
                    <img
                      src={logoPreview}
                      alt="Company Logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-lg border-2 border-dashed border-[var(--color-border)] flex items-center justify-center bg-[var(--color-muted)]">
                    <Icon name="Image" size={32} color="var(--color-muted-foreground)" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button
                    as="span"
                    variant="outline"
                    size="sm"
                    iconName="Upload"
                  >
                    اختر شعار
                  </Button>
                </label>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  الصيغ المدعومة: PNG, JPG, SVG, WEBP (الحد الأقصى 2 ميجابايت)
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[var(--color-secondary)]/10 rounded-lg flex items-center justify-center">
            <Icon name="Droplet" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-[var(--color-foreground)]">
              إعدادات العلامة المائية
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              إضافة علامة مائية شفافة على الصور والفيديوهات
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Checkbox
            label="تفعيل العلامة المائية"
            description="إضافة شعار الشركة كعلامة مائية على المستندات والوسائط"
            checked={branding?.watermarkEnabled}
            onChange={(e) => handleChange('watermarkEnabled', e?.target?.checked)}
          />

          {branding?.watermarkEnabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  شفافية العلامة المائية: {(branding?.watermarkOpacity * 100)?.toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={branding?.watermarkOpacity}
                  onChange={(e) => handleChange('watermarkOpacity', parseFloat(e?.target?.value))}
                  className="w-full h-2 bg-[var(--color-muted)] rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <Select
                label="موضع العلامة المائية"
                options={watermarkPositionOptions}
                value={branding?.watermarkPosition}
                onChange={(value) => handleChange('watermarkPosition', value)}
              />
            </>
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="flex items-center justify-end gap-3 p-4 bg-[var(--color-muted)] rounded-lg border border-[var(--color-border)]">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={saving || uploading}
          >
            إلغاء
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Save"
            onClick={handleSave}
            disabled={saving || uploading}
          >
            {saving || uploading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyBrandingSettings;